import { useCallback, useMemo, useState } from 'react'
import { Analytics } from '@genoa/analytics'
import { FlexLinks, PaymentCategory, SLCType } from '@genoa/domain'
import { calculateProcessingFee } from '@genoa/domain/lib/offers/calculate-processing-fee'
import { PaymentSchedule } from '@genoa/middle-end'
import { getDateTimeFromISO, toDollars } from '@genoa/utils'
import { Box } from '@chakra-ui/react'

import { useModal } from '../../../../../../contexts'
import { useSecureLineOfCredit } from '../../../../../../hooks'
import { useCustomerStatusState } from '../../../../../../modules/flex2/use-state'
import { useAnalytics, useHelpLinks } from '../../../../../../providers'
import { Gap, InlineButton, SmallText } from '../../../../../components'
import { getPaymentLabelsFactory } from './getPaymentLabels'
import { PaymentItem } from './PaymentScheduleTable'

interface UsePaymentItemsProps {
  payments: PaymentSchedule[]
  processingFeePercentage: number
  skipFees?: boolean
}

export const usePaymentItems = (props: UsePaymentItemsProps) => {
  const modal = useModal()
  const helpLinks = useHelpLinks()
  const analytics = useAnalytics()
  const { isFlexAnywhere } = useCustomerStatusState()
  const { isEnabledForSLC, isSimplifiedPaymentsEnabled, slcType } = useSecureLineOfCredit()
  const [isFeeInfoModalOpen, setIsFeeInfoModalOpen] = useState(false)

  const firstPaymentSchedule = useMemo(
    () => props.payments.find((p) => p.payment_category === PaymentCategory.FIRST_PAYMENT),
    [props.payments]
  )

  const getPaymentLabel = useCallback(getPaymentLabelsFactory(isSimplifiedPaymentsEnabled, slcType), [
    isSimplifiedPaymentsEnabled,
    firstPaymentSchedule,
  ])

  const mapPaymentToScheduleItem = (payment: PaymentSchedule, index: number = 0) => {
    const baseAmount = payment.base_amount || payment.amount
    const fee = calculateProcessingFee(props.processingFeePercentage, baseAmount)
    const propertyPassthroughFee = payment.property_passthrough_fee_amount || 0

    return {
      ...payment,
      amountBeforeFee: baseAmount,
      amount: props.skipFees ? payment.amount : fee + baseAmount + propertyPassthroughFee,
      day: getDateTimeFromISO(payment.bill_date || '').day,
      label: getPaymentLabel(payment, index).paymentLabel,
      showProcessingFeeDisclaimer: !props.skipFees || Boolean(payment.processing_fee),
    }
  }

  const firstPayment = useMemo<PaymentItem | undefined>(() => {
    if (!firstPaymentSchedule) {
      return
    }

    let description = 'When rent is due'

    if (firstPaymentSchedule.is_security_deposit) {
      description = slcType === SLCType.FLEX_ANYWHERE ? 'Deposit' : 'Today'
    } else if (isEnabledForSLC && !isSimplifiedPaymentsEnabled) {
      description = ''
    }

    return {
      ...mapPaymentToScheduleItem(firstPaymentSchedule),
      description,
      hideQuestionMark: true,
    }
  }, [mapPaymentToScheduleItem, firstPaymentSchedule, isEnabledForSLC, isSimplifiedPaymentsEnabled])

  const restPayments = useMemo(() => {
    return props.payments
      .filter((p) => p.payment_category === PaymentCategory.SECOND_PAYMENT)
      .map(mapPaymentToScheduleItem)
  }, [props.payments, mapPaymentToScheduleItem])

  const membershipFee = useMemo(() => {
    return props.payments.find((payment) => payment.payment_category === PaymentCategory.MEMBERSHIP_FEE)?.amount || 0
  }, [props.payments])

  const schedule = useMemo<PaymentItem[]>(
    () => [firstPayment, ...restPayments].filter((p): p is PaymentItem => p !== undefined),
    [firstPayment, restPayments]
  )

  const rentAmount = useMemo(() => {
    const amount = schedule.reduce((total, payment) => total + payment.amountBeforeFee, 0)
    return `$${toDollars(amount)}`
  }, [schedule])

  const handlePressMembershipFeeInfo = (analyticsEvent: Analytics.Events) => {
    analytics.logEvent(analyticsEvent)
    const onLearnMorePress = () => {
      helpLinks.open(FlexLinks.membershipFeeLearnMore)
    }
    const handleMembershipInfoText = () => {
      if (isFlexAnywhere) {
        return 'The membership fee only applies for each month you use Flex to pay rent.'
      }
      return 'Your membership will automatically renew each month until you cancel.'
    }

    modal.show({
      title: 'Membership fee',
      cta: 'Close',
      render: () => {
        return (
          <Box>
            <SmallText>{handleMembershipInfoText()}</SmallText>
            <Gap size="spacing_100" />
            <InlineButton onClick={onLearnMorePress}>Learn more about membership</InlineButton>
          </Box>
        )
      },
    })
    setIsFeeInfoModalOpen(true)
  }

  const handlePressCloseMembershipFeeInfoModal = () => {
    analytics.logEvent(Analytics.Events.OUT_OF_RANGE_PAYMENT_SCHEDULE_FEE_INFO_MODAL_CLOSE_CLICKED)
  }

  return {
    membershipFeeValue: membershipFee,
    handlePressMembershipFeeInfo,
    onPressCloseMembershipFeeInfoModal: handlePressCloseMembershipFeeInfoModal,
    isFeeInfoModalOpen,
    schedule,
    rentAmount,
  }
}
